import type { ColumnDef } from "@tanstack/react-table";
import { Badge } from "@/components/ui/base/badge";
import { Button } from "@/components/ui/base/button";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/base/tooltip";
import type { AdminUser } from "@/application/admins/types";
import { GoogleLoginCell } from "./GoogleLoginCell";

function formatDate(iso: string): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export function buildAdminsColumns(
  currentUserEmail: string | null,
  onDeactivate: (row: AdminUser) => void,
): ColumnDef<AdminUser, unknown>[] {
  return [
    {
      accessorKey: "email",
      header: "Email",
      cell: ({ row }) => (
        <span className="text-foreground font-medium">
          {row.getValue("email")}
        </span>
      ),
    },
    {
      id: "name",
      header: "Name",
      accessorFn: (row) => `${row.firstName} ${row.lastName}`.trim(),
      cell: ({ getValue }) => (
        <span className="text-foreground text-sm">
          {(getValue() as string) || "—"}
        </span>
      ),
    },
    {
      accessorKey: "status",
      header: "Status",
      cell: ({ row }) => {
        const status = row.getValue("status") as AdminUser["status"];
        return (
          <Badge variant={status === "active" ? "success" : "destructive"}>
            {status === "active" ? "Active" : "Disabled"}
          </Badge>
        );
      },
    },
    {
      accessorKey: "hasGoogleLogin",
      header: "Google",
      cell: ({ row }) => (
        <GoogleLoginCell hasGoogleLogin={row.original.hasGoogleLogin} />
      ),
    },
    {
      accessorKey: "adminProvisioned",
      header: "Provisioned",
      cell: ({ row }) =>
        row.getValue("adminProvisioned") ? (
          <Badge variant="info">Admin Provisioned</Badge>
        ) : null,
    },
    {
      accessorKey: "createdAt",
      header: "Created",
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDate(row.getValue("createdAt"))}
        </span>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        const admin = row.original;
        const isSelf =
          currentUserEmail !== null &&
          admin.email.toLowerCase() === currentUserEmail.toLowerCase();
        const isDisabled = admin.status === "disabled";
        const blocked = isSelf || isDisabled;

        const tooltipText = isSelf
          ? "You cannot deactivate your own account"
          : isDisabled
            ? "Already disabled"
            : undefined;

        const btn = (
          <Button
            variant="outline"
            size="sm"
            disabled={blocked}
            onClick={blocked ? undefined : () => onDeactivate(admin)}
          >
            Deactivate
          </Button>
        );

        if (!tooltipText) return btn;

        return (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="inline-flex">{btn}</span>
              </TooltipTrigger>
              <TooltipContent side="left">{tooltipText}</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        );
      },
    },
  ];
}
